import * as Yup from 'yup';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import {
  Grid,
  TextField,
  Alert,
  Typography,
  Switch,
  Stack,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// component
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import client from '../../api/clients';
import { uploadImagedData } from 'src/api/addAgent';
import TrueEventsAGC from 'src/pages/AgentConsolidated/TrueEventsAGC';
import { MdOutlineImageNotSupported } from "react-icons/md";
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// ----------------------------------------------------------------------
export default function ClientForm({ total, dialogData, setOpen, reloadClientsData,dataChecker,isReseller }) {
  
  const [fullWidth, setFullWidth] = useState(true);
  const [maxWidth, setMaxWidth] = useState('md');
  const clients = [...total].sort((a, b) => b.ID - a.ID);
  const getClientGroupId = (clientItem) => {
    const itemId = Number(clientItem?.ID || clientItem?.Id || 0);
    const assignedClientId = Number(clientItem?.AssignClient || 0);
    return assignedClientId || itemId;
  };
  const addLocationClientOptions = useMemo(() => {
    
    const clientsByDisplayName = new Map();

    clients.forEach((clientItem) => {
      const displayNameKey = String(clientItem?.displayname || '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
      if (!displayNameKey) return;

      const groupedClients = clientsByDisplayName.get(displayNameKey) || [];
      groupedClients.push(clientItem);
      clientsByDisplayName.set(displayNameKey, groupedClients);
    });

    const mainClients = [];

    clientsByDisplayName.forEach((groupedClients) => {
      if (groupedClients.length === 1) {
        mainClients.push(groupedClients[0]);
        return;
      }

      const validMainClient = groupedClients.find((candidate) => {
        const candidateId = Number(candidate?.ID || 0);
        const candidateAssignedClient = Number(candidate?.AssignClient || 0);

        // Main client must have its ID equal to its own AssignClient ID
        if (!candidateId || candidateAssignedClient !== candidateId) return false;

        // All remaining clients must have their AssignClient equal to the main client's ID
        const remainingClients = groupedClients.filter(
          (clientItem) => clientItem.ID !== candidateId
        );

        return remainingClients.every(
          (clientItem) => Number(clientItem?.AssignClient || 0) === candidateId
        );
      });

      if (validMainClient) {
        mainClients.push(validMainClient);
      }
    });

    return mainClients;
  }, [clients]);
  const [selectedClient, setSelectedClient] = useState(null);
  const isEditMode = Boolean(dialogData.id);
  const formMode = dialogData.mode || (isEditMode ? 'edit' : 'addClient');
  const isAddLocationMode = !isEditMode && formMode === 'addLocation';
  const isLocationValidationMode = isAddLocationMode;
  const locationOwnerClientId = selectedClient?.ID || dialogData.id?.AssignClient || 0;
  const existingLocationsForValidation = locationOwnerClientId
    ? total
        .filter((c) => c.AssignClient === locationOwnerClientId || c.ID === locationOwnerClientId)
        .map((c) => ({
          ID: c.ID,
          Location: c.Location || ''
        }))
    : [];
  const dialogTitle = isEditMode ? 'Edit Client' : isAddLocationMode ? 'Add Location' : 'Add Client';

  const clientValue = localStorage.getItem('client_type');
  const defaultIsAgentBased = isReseller && String(clientValue || '').toUpperCase() === 'INDIA' ? 0 : 1;
  const resetAll = () => {
    setImage(null);
    setLogo(null);
    setFinalImage('');
    setFinalLogo('');
    setIsDisabled(false);
    setIsDisabledLogo(false);
  };

  const handleClose = () => {
    formik.resetForm();
    setSelectedClient(null);
    setSelectedClientError('');
    setErrorMsg({ status: false, msg: "", severity: "error" });
    setDisableField(false);
    setOpen({ ...dialogData, isOpen: false, id: null, mode: 'addClient' });
    resetAll();
  };

  const [disableField, setDisableField] = useState(false);

  const [isError, setErrorMsg] = useState({ status: false, msg: "", severity: "error" })
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const [timezones, setTimezone] = useState([])
  const [timezone, setZone] = useState('')

  const [clientType, setClientType] = useState('')
  const [environmentType, setEnvironmentType] = useState('')
  const [, setIsNeed2FAAuthentication] = useState('')
  const [clientNameExits, setClientNameExit] = useState('')
  const [isDisabled, setIsDisabled] = useState(false);
  const [isDisabledLogo, setIsDisabledLogo] = useState(false);
  const [image, setImage] = useState(null);
  const [finalImageURL, setFinalImage] = useState('')
  const [imageError, setImageError] = useState('')

  const [logo, setLogo] = useState(null);
  const [finalLogoURL, setFinalLogo] = useState('');
  const [logoError, setLogoError] = useState('');
  const [selectedClientError, setSelectedClientError] = useState('');

  const [dialogImage, setDialogImage] = useState(false)
  const [dialogLogo, setDialogLogo] = useState(false)
  const requiredLabelSx = { '& .MuiFormLabel-asterisk': { color: 'error.main' } };


  useEffect(() => {
    client.getTimeZones((response) => {
      if (response.status === 200) {
        setTimezone(response.data)
      }
    })

  }, [])

  const handleTimeZone = (event) => {
    setZone(event.target.value)
  }

  const clearFieldValidation = (fieldName) => {
    setFieldTouched(fieldName, false);
    setFieldError(fieldName, undefined);
  };

  const toDayjsOrNull = (value) => {
    if (!value) return null;
    return dayjs(value);
  };

  const handleClients = (value) => {
    
    const clientId = value;
    console.log('clientId:', clientId)
    setErrorMsg({ status: false, msg: "", severity: "error" });
    // If cleared selection
    if (!clientId) {
      setSelectedClient(null);
      setSelectedClientError('');
      setDisableField(false);
      setFieldValue("Location", "");

      // Reset auto filled fields
      setFieldValue("clientName", "");
      setFieldValue("profileName", "");
      setFieldValue("email", "");
      setFieldValue("address", "");
      setFieldValue("NoOfCameras", "");
      setFieldValue("timezone", "");
      setFieldValue("ClientType", "");
      setFieldValue("userId", "");
      setFieldValue("userPwd", "");
      setFieldValue('IsAgentBased', defaultIsAgentBased);
      setFieldValue("MonitoringStartDate", null);
      setFieldValue("MonitoringEndDate", null);
      setFieldValue("Location", "");
      setFieldValue("EnvironmentType", 1);
      setFieldValue("IsNeed2FAAuthentication", 0)
      setIsNeed2FAAuthentication('0');
      setFieldValue("BillingMonitoringHours", "");
      setImage(null);
      setLogo(null);
      setFinalImage("");
      setFinalLogo("");
      return;
    }

    const clientObj = clients.find(c => c.ID === clientId);
    setSelectedClient(clientObj);
    setSelectedClientError('');
    console.log("selected client:", clientObj);

    if (clientObj) {
      setFieldValue("clientName", clientObj.displayname);
      clearFieldValidation("clientName");
      setFieldValue("profileName", clientObj.ProfileName);
      clearFieldValidation("profileName");
      setFieldValue("email", clientObj.Email);
      clearFieldValidation("email");
      setFieldValue("address", clientObj.Address);
      clearFieldValidation("address");
      setFieldValue("NoOfCameras", clientObj.NoOfCameras ?? '');
      clearFieldValidation("NoOfCameras");
      setFieldValue("timezone", clientObj.TimezoneId);
      clearFieldValidation("timezone");
      setFieldValue("ClientType", clientObj.ClientType);
      clearFieldValidation("ClientType");
      if (isAddLocationMode) {
        setFieldValue("userId", "");
        setFieldValue("userPwd", "");
      } else {
        setFieldValue("userId", clientObj.Name);
        clearFieldValidation("userId");
        setFieldValue("userPwd", clientObj.Password);
        clearFieldValidation("userPwd");
      }
      setFieldValue('IsAgentBased', clientObj.IsAgentBased);
      setFieldValue("MonitoringStartDate", toDayjsOrNull(clientObj.MonitoringStartDate));
      setFieldValue("MonitoringEndDate", toDayjsOrNull(clientObj.MonitoringEndDate));
      setFieldValue("BillingMonitoringHours", normalizeBillingMonitoringHours(clientObj.BillingMonitoringHours));
      setFieldValue("EnvironmentType", clientObj.EnvironmentType);
      clearFieldValidation("EnvironmentType");
      setFieldValue("IsNeed2FAAuthentication", clientObj.IsNeed2FAAuthentication);
      setIsNeed2FAAuthentication(String(clientObj.IsNeed2FAAuthentication ?? 0));
      clearFieldValidation("IsNeed2FAAuthentication");

      if (clientObj.Image) {
        setFinalImage(clientObj.Image);
        const dummyImageFile = new File([], clientObj.Image.split('/').pop());
        setImage(dummyImageFile);
        setDialogImage(true)
      } else {
        setImage(null);
        setFinalImage('');
      }

      if (clientObj.LogoUrl) {
        setFinalLogo(clientObj.LogoUrl);
        const dummyLogoFile = new File([], clientObj.LogoUrl.split('/').pop());
        setLogo(dummyLogoFile);
        setDialogLogo(true)
      } else {
        setLogo(null);
        setFinalLogo('');
      }
    }

    setDisableField(false);
  };


  const handleClientType = (event) => {
    // console.log("client type",event.target.value)
    setClientType(event.target.value)
  }

  const handleEnvironmentType = (event) => {
    // console.log("client type",event.target.value)
    setEnvironmentType(event.target.value)
  }

  const handleAuthentication = (event) => {
    const { value } = event.target;
    setIsNeed2FAAuthentication(value)
    setFieldValue('IsNeed2FAAuthentication', value);
    if (String(value) !== '1') {
      clearFieldValidation('email');
    }
  }

  const normalizeBillingMonitoringHours = (value) => {
  if (!value) return '';
  return value
    .replace(/\\r\\n/g, '\n')
    .replace(/\\xa0/g, ' ');
  };


  const currentClientId = Number(dialogData.id?.ID || 0);
  const totalForDuplicateValidation = currentClientId
    ? (total || []).filter((item) => Number(item?.ID || 0) !== currentClientId)
    : (total || []);
  const dataCheckerForDuplicateValidation = currentClientId
    ? (dataChecker || []).filter((item) => Number(item?.Id ?? item?.ID ?? 0) !== currentClientId)
    : (dataChecker || []);

  const ClientSchema = Yup.object().shape({
  clientName: (isAddLocationMode ? Yup.string() : Yup.string().required('Client/Company Name is required'))
    .test(
      'unique-client-name',
      'Client name already exists.',
      function(value) {
        const normalizedValue = String(value || '').trim().toLowerCase();
        if (!normalizedValue || isAddLocationMode) return true;

        const currentClientId = Number(dialogData.id?.ID || 0);
        const currentClientGroupId = Number(dialogData.id?.AssignClient || dialogData.id?.ID || 0);

        return !(total || []).some(
          (item) =>
            String(item?.displayname || '').trim().toLowerCase() === normalizedValue &&
            Number(item?.ID || 0) !== currentClientId &&
            getClientGroupId(item) !== currentClientGroupId
        );
      }
    ),
  profileName: Yup.string().required('Profile Name is required'),
  NoOfCameras: Yup.number()
    .typeError('No of Cameras is required')
    .required('No of Cameras is required'),
  timezone: Yup.string().required('Timezone is required'),
  userId: Yup.string()
    .required('Username is required')
    .test(
      'unique-username',
      'Username already exists.',
      function(value) {
        if (!value) return true;
        const normalizedValue = String(value).trim().toLowerCase();

        const userNameExistsInDataChecker = dataCheckerForDuplicateValidation.some(
          (item) => String(item?.UserName ?? '').trim().toLowerCase() === normalizedValue
        );
        const userNameExistsInTotal = totalForDuplicateValidation.some(
          (item) => String(item?.Name ?? '').trim().toLowerCase() === normalizedValue
        );

        return !(userNameExistsInDataChecker || userNameExistsInTotal);
      }
    ),
  // email: Yup.string().when('IsNeed2FAAuthentication', {
  //   is: (value) => String(value) === '1',
  //   then: (schema) => schema.required('Email is required'),
  //   otherwise: (schema) => schema.notRequired()
  // }),
  userPwd: Yup.string()
    .required('Password is required')
    .test(
      'unique-password',
      'Password already exists.',
      function(value) {
        if (!value) return true;
        const normalizedValue = String(value).trim();

        const passwordExistsInDataChecker = dataCheckerForDuplicateValidation.some(
          (item) => String(item?.Password ?? '').trim() === normalizedValue
        );
        const passwordExistsInTotal = totalForDuplicateValidation.some(
          (item) => String(item?.Password ?? '').trim() === normalizedValue
        );

        return !(passwordExistsInDataChecker || passwordExistsInTotal);
      }
    ),
  ClientType: isReseller
    ? Yup.string()
    : Yup.string().required('Please select Client Type'),
  Location: isLocationValidationMode
    ? Yup.string()
        .required('Location is required')
        .test(
          'unique-location',
          'Location name already exists for this client',
          function(value) {
            if (!value) return true;
            const normalizedValue = value.trim().toLowerCase();
            if (!normalizedValue) return true;

            const currentLocationId = Number(dialogData.id?.ID || 0);

            const locationExists = existingLocationsForValidation?.some(
              (loc) =>
                (loc.Location || '').trim().toLowerCase() === normalizedValue &&
                Number(loc.ID || 0) !== currentLocationId
            );

            return !locationExists;
          }
        )
    : Yup.string(),
  EnvironmentType: Yup.string(),
  BillingMonitoringHours: Yup.string(),
  IsNeed2FAAuthentication: Yup.string(),
  MonitoringStartDate: Yup.date().nullable(),
  MonitoringEndDate: Yup.date().nullable()
    .min(
      Yup.ref('MonitoringStartDate'),
      'End date must be after start date'
    )
});


  const jsonToParam = (data) => {
    return Object.keys(data).map(function (k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(data[k])
    }).join('&')
  }


  const handelFormResponse = (response) => {
    setSubmitting(false)
    if (response.status === 200) {
      // console.log(response.data)
      resetForm()
      handleClose()
      reloadClientsData()
    }
  }

  const formatDate = (date) => {
    if (!date && date !== '') return '';

    // Check if it's a Dayjs object - use dayjs.isDayjs() properly
    if (date && typeof date === 'object' && dayjs.isDayjs(date)) {
      return date.format('YYYY-MM-DD');
    }

    // If it's already a YYYY-MM-DD string
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    // If it's an ISO string
    if (typeof date === 'string' && date.includes('T')) {
      return date.split('T')[0];
    }

    // Return empty string for any other case
    return '';
  };

  const formik = useFormik({
    initialValues: {
      id: null,
      clientName: "",
      profileName: "",
      email: "",
      address: "",
      NoOfCameras: '',
      timezone: "",
      userId: "",
      userPwd: "",
      ClientType: "",
      IsAgentBased: defaultIsAgentBased,
      LogoUrl: '',
      Image: '',
      Location: '',
      BillingMonitoringHours: '',
      MonitoringStartDate: '',
      MonitoringEndDate: '',
      EnvironmentType:1,
      IsNeed2FAAuthentication:0,
    },
    validationSchema: ClientSchema,
    validateOnChange: true,
    validateOnBlur: true,
	    onSubmit: () => {
	      if (isAddLocationMode && !selectedClient) {
	        setSelectedClientError("Select Client is required");
	        setSubmitting(false);
        return;
      }

      if (isLocationValidationMode && !(values.Location || '').trim()) {
        setFieldTouched("Location", true, false);
        setFieldError("Location", "Location is required when using existing client");
        setSubmitting(false);
        return;
      }

	      if (isLocationValidationMode) {
	        const normalizedLocation = (values.Location || '').trim().toLowerCase();
	        const currentLocationId = Number(values.id || dialogData.id?.ID || 0);
	        const hasDuplicateLocation = existingLocationsForValidation.some(
          (loc) =>
            (loc.Location || '').trim().toLowerCase() === normalizedLocation &&
            Number(loc.ID || 0) !== currentLocationId
        );

        if (hasDuplicateLocation) {
          setFieldTouched("Location", true, false);
          setFieldError("Location", "Location name already exists for this client");
          setSubmitting(false);
          return;
	        }
	      }

	      const enteredEmail = (values.email || '').trim();
	      if (isEmailAuthenticationRequired && !enteredEmail) {
	        setFieldTouched('email', true, false);
	        setFieldError('email', 'Email is required');
	        setSubmitting(false);
	        return;
	      }

	      if (!isEmailAuthenticationRequired) {
	        clearFieldValidation('email');
	      }

	      const enteredClientName = String(values.clientName || '').trim().toLowerCase();
	      const currentId = Number(values.id || 0);
	      const currentClientGroupId = Number(dialogData.id?.AssignClient || values.id || 0);
      const isClientNameDuplicate = !isAddLocationMode && (total || []).some(
        (item) =>
          String(item?.displayname || '').trim().toLowerCase() === enteredClientName &&
          Number(item?.ID || 0) !== currentId &&
          getClientGroupId(item) !== currentClientGroupId
      );

      if (isClientNameDuplicate) {
        setFieldTouched('clientName', true, false);
        setFieldError('clientName', 'Client name already exists.');
        setSubmitting(false);
        return;
      }

      if (isAddLocationMode && selectedClient && !(selectedClient.Location || '').trim()) {
        const selectedClientName = (selectedClient.displayname || '').trim() || 'Selected client';
        setErrorMsg({
          status: true,
          msg: `${selectedClientName}: Before updating the location to the client, please go to the Edit option and update the location for the already existing client`,
          severity: 'warning'
        });
        setSubmitting(false);
        return;
      }

      setErrorMsg((er) => {
        // console.log('er',er)
        er.status = false
        er.severity = 'error'
        return er
      })

      const assignClientId = selectedClient
        ? selectedClient.ID
        : (dialogData.id?.ID || 0);

        let Type = ''
        if(isReseller){
          Type = clientValue
        }

	      const trimmedEmail = (values.email || '').trim();
	      const emailForInsert = isEmailAuthenticationRequired ? trimmedEmail : (trimmedEmail || '');
	      const is2FARequiredForInsert = isEmailAuthenticationRequired ? 1 : 0;

      const insertData = {
        Name: values.userId,
        Password: values.userPwd,
        Email: emailForInsert,
        Address: values.address ? values.address : '',
        NoOfCameras: values.NoOfCameras,
        UserType: 2,
        displayname: values.clientName,
        ProfileName: values.profileName,
        TimezoneId: values.timezone,
        IsAgentBased: values.IsAgentBased,
        ClientType: isReseller ? Type : values.ClientType,
        Image: finalImageURL,
        LogoUrl: finalLogoURL,
        Location: values.Location,
        BillingMonitoringHours: values.BillingMonitoringHours,
        AssignClient: assignClientId,
        MonitoringStartDate: formatDate(values.MonitoringStartDate),
        MonitoringEndDate: formatDate(values.MonitoringEndDate),
        EnvironmentType: values.EnvironmentType,
        IsNeed2FAAuthentication: is2FARequiredForInsert,
      }

      console.log('Inserted Values', insertData)
      if (values.id) {
        insertData.ID = values.id
        client.updateClient(insertData, (response) => handelFormResponse(response))
      } else
        client.addClient(insertData, (response) => handelFormResponse(response))

      // axios({
      //   method: 'post',
      //   url: '/token',
      //   data: jsonToParam(values),
      //   validateStatus: function (status) {
      //     return status >= 200 || status === 400;
      //   }
      // }).then(function (response) {
      //   setSubmitting(false)
      //   if (response.status === 200 || response.status === 201) {

      //     navigate('/', { replace: true })
      //   } else
      //     setErrorMsg({ status: true, msg: (response.data.error_description ?? 'The user name or password is incorrect') })
      // })

    }
  });

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    handleBlur,
    getFieldProps,
    setSubmitting,
    setFieldValue,
    resetForm,
    setFieldTouched,
    setFieldError,
    submitCount
  } = formik;
  const isEmailAuthenticationRequired = String(values.IsNeed2FAAuthentication) === '1';


  const handleSwitch = (event) => {
    setFieldValue('IsAgentBased', parseInt(event.target.value) === 0 ? 1 : 0)
  }
  const handleSelect = (event) => {
    // console.log(event.target.value);
  };

  useEffect(() => {
    const keys = ['id', 'clientName', 'profileName', 'Location', 'email', 'address', 'NoOfCameras', 'EnvironmentType', 'IsNeed2FAAuthentication', 'timezone', 'userId', 'userPwd', 'IsAgentBased', 'ClientType', 'Image', 'LogoUrl', 'MonitoringStartDate', 'MonitoringEndDate','BillingMonitoringHours']
    const values = ['ID', 'displayname', 'ProfileName', 'Location', 'Email', 'Address', 'NoOfCameras', 'EnvironmentType', 'IsNeed2FAAuthentication', 'TimezoneId', 'Name', 'Password', 'IsAgentBased', 'ClientType', 'Image', 'LogoUrl', 'MonitoringStartDate', 'MonitoringEndDate','BillingMonitoringHours']

    // console.log('dialog.id', dialogData)
    if (dialogData.id) {
      // keys.forEach((key, idx) => {
      //   // console.log(key, dialogData.id[values[idx]] === null ? '' : dialogData.id[values[idx]])
      //   setFieldValue(key, dialogData.id[values[idx]] === null ? '' : dialogData.id[values[idx]])
      // })

      keys.forEach((key, idx) => {
        const apiValue = dialogData.id[values[idx]];

        if (key === 'IsNeed2FAAuthentication' && apiValue === undefined) {
          setFieldValue('IsNeed2FAAuthentication', 0);
          setIsNeed2FAAuthentication('0');
          return;
        }

        if ((key === 'MonitoringStartDate' || key === 'MonitoringEndDate')) {
            if (!apiValue) {
              setFieldValue(key, null);
            } else {
              setFieldValue(key, dayjs(apiValue)); 
            }
          } else if (key === 'BillingMonitoringHours') {
            setFieldValue(key, normalizeBillingMonitoringHours(apiValue));
          } else if (key === 'IsNeed2FAAuthentication') {
            setFieldValue(key, apiValue ?? 0);
            setIsNeed2FAAuthentication(String(apiValue ?? 0));
          } else {
            setFieldValue(key, apiValue ?? '');
          }
        })

      if (dialogData.id.Image) {
        setFinalImage(dialogData.id.Image);
        const dummyImageFile = new File([], dialogData.id.Image.split('/').pop());
        setImage(dummyImageFile);
        setDialogImage(true)
      }

      if (dialogData.id.LogoUrl) {
        setFinalLogo(dialogData.id.LogoUrl);
        const dummyLogoFile = new File([], dialogData.id.LogoUrl.split('/').pop());
        setLogo(dummyLogoFile);
        setDialogLogo(true)
      }

      if (dialogData.id.AssignClient) {
        const assignedClient = clients.find(c => c.ID === dialogData.id.AssignClient);
        if (assignedClient) {
          setSelectedClient(assignedClient);
          setDisableField(false);
        }
      }
    } else {
      resetForm();
      setFieldValue('IsAgentBased', defaultIsAgentBased);
      setIsNeed2FAAuthentication('0');
    }
  }, [dialogData.id, defaultIsAgentBased, resetForm, setFieldValue])

  const cleanFilename = (filename) => {
    if (!filename) return filename;

    const ext = filename.split('.').pop();
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");

    const cleaned = [...new Set(
      nameWithoutExt
        .replace(/[^a-zA-Z]/g, '')  // only alphabets
        .toLowerCase()              // force lowercase
        .split('')                  // into characters
    )]
      .sort()                       // sort alphabetically
      .join('');

    return cleaned + (ext ? `.${ext}` : '');
  };

  const formatClientName = (name) => {
    if (!name) return "client";
    return name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  };




  const handleImageChange = (event) => {

    const file = event.target.files[0];
    if (!file) return;

    const name = formik.values.clientName;
    //   if (!name || name.trim() === "") {
    //   // Set error directly on clientName field
    //   formik.setFieldError("clientName", "Please enter client name before uploading an image");
    //   return; // stop further execution
    // }

    setDialogImage(false);
    setIsDisabled(true);

    // Clean filename ONLY for new uploads (not edit mode)
    // const finalFile = dialogData.id ? file : new File(
    //   [file],
    //   cleanFilename(file.name),
    //   { type: file.type }
    // );



    const clientName = formatClientName(name);
    const ext = file.name.split('.').pop(); // file extension
    const renamedFile = new File([file], `${clientName}_profile.${ext}`, { type: file.type });

    setImage(renamedFile);
    formik.setFieldValue('Image', renamedFile);
    setImageError('');
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const name = formik.values.clientName;

    setDialogLogo(false);
    setIsDisabledLogo(true);

    // Clean filename ONLY for new uploads (not edit mode)
    // const finalFile = dialogData.id ? file : new File(
    //   [file],
    //   cleanFilename(file.name),
    //   { type: file.type }
    // );

    const clientName = formatClientName(name);
    const ext = file.name.split('.').pop(); // file extension
    const renamedFile = new File([file], `${clientName}_logo.${ext}`, { type: file.type });

    setLogo(renamedFile);
    formik.setFieldValue('LogoUrl', renamedFile);
    setLogoError('');
  };

  //  const handleImageChange = (event) => {

  //     const file = event.target.files[0];
  //     if (!file) {
  //       return;
  //     }
  //     setDialogImage(false)
  //     setIsDisabled(true)
  //     setImage(file)
  //     formik.setFieldValue('Image', file); 
  //     setImageError('');
  //   };

  //   const handleLogoChange = (event) => {
  //     const file = event.target.files[0];
  //     if (!file) return;
  //     setDialogLogo(false)
  //     setIsDisabledLogo(true);
  //     setLogo(file);
  //     formik.setFieldValue('LogoUrl', file); 
  //     setLogoError(''); 
  //   };

  useEffect(() => {
    if (!dialogImage) {
      setImageError('');
      const uploadImage = (imageFile) => {
        const fileName = imageFile.name;
        const image_file = {
          file_path: `Client/Image`,
          file: imageFile
        };
        uploadImagedData(image_file, (response) => {
          if (response.status === 200) {
            const newImagePath = response.data.url
            setFinalImage(newImagePath)

            setIsButtonDisabled(false);
            setIsDisabled(false)

          }
        });
      };

      if (image != null) {
        setIsButtonDisabled(true);
        setIsDisabled(true)
        uploadImage(image);
      }
    }
  }, [image]);

  useEffect(() => {
    if (!dialogLogo) {
      setLogoError('');
      const uploadLogo = (logoFile) => {
        const fileName = logoFile.name;
        const logo_file = {
          file_path: `Client/Logo`,
          file: logoFile
        };
        uploadImagedData(logo_file, (response) => {
          if (response.status === 200) {
            const newLogoPath = response.data.url;
            setFinalLogo(newLogoPath);
            setIsButtonDisabled(false);
            setIsDisabledLogo(false);
          }
        });
      };

      if (logo != null) {
        setIsButtonDisabled(true);
        setIsDisabledLogo(true);
        uploadLogo(logo);
      }
    }
  }, [logo]);

  const handleDateChange = (field, value) => {
    //const shouldValidate = !!value;
    //setFieldValue(field, value, shouldValidate);
    setFieldValue(field, value);
  };

  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={dialogData.isOpen}
      onClose={handleClose}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>        
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Typography variant='subtitle2' sx={{ my: 2 }}>Basic Info</Typography>
              <Grid container spacing={2}>
                  {!isAddLocationMode && (<Grid item md={6}>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="upload-logo"
                      type="file"
                      onChange={handleLogoChange}
                    />
                    <TextField
                      fullWidth
                      autoComplete="clientName"
                      type="text"
                      label="Client/Company Name"
                      required
                      InputLabelProps={{ shrink: true }}
                      sx={requiredLabelSx}
                      {...getFieldProps('clientName')}
                      error={Boolean(touched.clientName && errors.clientName)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <label
                              htmlFor="upload-logo"
                              onClick={(e) => {
                                if (!formik.values.clientName || formik.values.clientName.trim() === "") {
                                  e.preventDefault();
                                  formik.setFieldTouched("clientName", true, false);
                                  formik.setFieldError("clientName", "Please enter client name before uploading an image");
                                }
                              }}
                            >
                              <Tooltip title="Upload client logo">
                                <IconButton
                                  component="span"
                                  size="small"
                                  sx={{
                                    bgcolor: 'success.lighter',
                                    color: 'success.dark',
                                    '&:hover': { bgcolor: 'success.light' }
                                  }}
                                >
                                  <BadgeOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </label>
                          </InputAdornment>
                        )
                      }}
                    />
                    {touched.clientName && errors.clientName && <Typography color="error" variant="body2"  >{touched.clientName && errors.clientName}</Typography>}
                    {isDisabledLogo && !dialogLogo && <CircularProgress size={20} sx={{ mt: 1 }} />}
                    {logo && finalLogoURL ? <Typography sx={{ fontSize: '12px', mt: 0.5 }}>{logo.name}</Typography> : null}
                  </Grid>)}

                  {isAddLocationMode && (
                    <Grid item md={6}>
                      <Autocomplete
                        value={selectedClient || null}
                        onChange={(event, newValue) => {
                          handleClients(newValue?.ID || null);
                        }}
                        options={addLocationClientOptions || []}
                        getOptionLabel={(option) => (option ? option.displayname : '')}
                        isOptionEqualToValue={(option, value) => option.ID === value.ID}
                        renderOption={(props, option) => (
                          <li {...props} key={option.ID}>
                            {option.displayname}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Client"
                            variant="outlined"
                            required
                            InputLabelProps={{ shrink: true }}
                            sx={requiredLabelSx}
                            error={Boolean(selectedClientError)}
                            helperText={selectedClientError}
                          />
                        )}
                      />
                    </Grid>
                  )}

                 
	                  <Grid item md={6}>
                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="upload-image"
                        type="file"
                        onChange={handleImageChange}
                      />
	                    <TextField
	                      fullWidth
	                      autoComplete="profileName"
	                      type="text"
                      label="Profile Name"
                      required
                      InputLabelProps={{ shrink: true }}
	                      sx={requiredLabelSx}
	                      {...getFieldProps('profileName')}
	                      error={Boolean(touched.profileName && errors.profileName)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <label
                                htmlFor="upload-image"
                                onClick={(e) => {
                                  if (!formik.values.clientName || formik.values.clientName.trim() === "") {
                                    e.preventDefault();
                                    formik.setFieldTouched("clientName", true, false);
                                    formik.setFieldError("clientName", "Please enter client name before uploading an image");
                                  }
                                }}
                              >
                                <Tooltip title="Upload profile image">
                                  <IconButton
                                    component="span"
                                    size="small"
                                    sx={{
                                      bgcolor: 'info.lighter',
                                      color: 'info.dark',
                                      '&:hover': { bgcolor: 'info.light' }
                                    }}
                                  >
                                    <AddPhotoAlternateOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </label>
                            </InputAdornment>
                          )
                        }}
	                    />
	                    {touched.profileName && errors.profileName && <Typography color="error" variant="body2"  >{touched.profileName && errors.profileName}</Typography>}
                      {isDisabled && !dialogImage && <CircularProgress size={20} sx={{ mt: 1 }} />}
                      {image && finalImageURL ? <Typography sx={{ fontSize: '12px', mt: 0.5 }}>{image.name}</Typography> : null}
	                  </Grid>

                  <Grid item md={6}>
                    <TextField
                      fullWidth
                      autoComplete="Location"
                      type="text"
                      label="Location"
                      required={isLocationValidationMode}
                      InputLabelProps={{ shrink: true }}
                      sx={requiredLabelSx}
                      {...getFieldProps('Location')}
                      error={Boolean((touched.Location || submitCount > 0) && errors.Location)}
                    />
                    {(touched.Location || submitCount > 0) && errors.Location && <Typography color="error" variant="body2"  >{errors.Location}</Typography>}
                  </Grid>

	                  <Grid item md={6}>
	                    <TextField
	                      fullWidth
	                      autoComplete="address"
                      type="text"
                      label="Address"
                      InputLabelProps={{ shrink: true }} 
                      {...getFieldProps('address')}
                      error={Boolean(touched.address && errors.address)}
                      //disabled={disableField}
                      // helperText={touched.address && errors.address}
                    />
                  {touched.address && errors.address && <Typography color="error" variant="body2"  >{touched.address && errors.address}</Typography>}

	                  </Grid>

	                  <Grid item md={6}>
	                    <TextField
	                      fullWidth
                      autoComplete="email"
                      type="text"
                      label="Email to Authenticate"
                      InputLabelProps={{ shrink: true }}
                      {...getFieldProps('email')}
                      error={Boolean(isEmailAuthenticationRequired && (touched.email || submitCount > 0) && errors.email)}
                      //disabled={disableField}
                      // helperText={touched.email && errors.email}
                    />
                    {isEmailAuthenticationRequired && (touched.email || submitCount > 0) && errors.email && <Typography color="error" variant="body2"  >{errors.email}</Typography>}

                  </Grid>
                
 <Grid item md={6}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-labell">Email Authentication</InputLabel>
	                  <Select
	                    labelId="demo-simple-select-label"
	                    id="demo-simple-select"
	                    name="IsNeed2FAAuthentication"
	                    value={String(values.IsNeed2FAAuthentication ?? 0)}
	                    label="Email Authentication"
	                    onChange={handleAuthentication}
	                    onBlur={handleBlur}
	                    error={Boolean(touched.IsNeed2FAAuthentication && errors.IsNeed2FAAuthentication)}
	                    // disabled={disableField}
	                  >
                    <MenuItem sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(143, 148, 148, 0.91)'
                                    }
                                }}  value="1">Required</MenuItem>
                    <MenuItem sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(143, 148, 148, 0.91)'
                                    }
                                }}  value="0">Not Required</MenuItem>
                    {/* <FormHelperText>{touched.timezone && errors.timezone}</FormHelperText> */}
                  </Select>
                  {touched.IsNeed2FAAuthentication && errors.IsNeed2FAAuthentication && <Typography color="error" variant="body2">{touched.IsNeed2FAAuthentication && errors.IsNeed2FAAuthentication}</Typography>}

                </FormControl>
              </Grid>
                 
                 
                  <Grid item md={6}>
                  <FormControl
                    fullWidth
                    required
                    error={Boolean(touched.timezone && errors.timezone)}
                    sx={requiredLabelSx}
                  >
                  <InputLabel
                    id="timezone-label"
                    required
                    shrink
                    error={Boolean(touched.timezone && errors.timezone)}
                    sx={requiredLabelSx}
                  >
                    Timezone
                  </InputLabel>
                  <Select
                    
                    labelId="timezone-label"
                    id="timezone-select"
                    value={timezone}
                    label="Timezone"
                    required
                    notched
                    onChange={handleTimeZone}
                    {...getFieldProps('timezone')}
                    error={Boolean(touched.timezone && errors.timezone)}
                    //disabled={disableField}
                    // helperText={touched.timezone && errors.timezone}
                  >
                    {timezones.map((item, key) => (
                      <MenuItem key={key} value={item.Id}>{item.TimeZone}</MenuItem>
                    ))}
                    
                    {/* <FormHelperText>{touched.timezone && errors.timezone}</FormHelperText> */}
                  </Select>
                  {touched.timezone && errors.timezone && <Typography color="error" variant="body2"  >{touched.timezone && errors.timezone}</Typography>}

                  </FormControl>
                  
                 
                  </Grid>
                 {!isReseller && <Grid item md={6}>
                  <FormControl
                    fullWidth
                    required
                    error={Boolean(touched.ClientType && errors.ClientType)}
                    sx={requiredLabelSx}
                  >
                  <InputLabel
                    id="client-type-label"
                    required
                    shrink
                    error={Boolean(touched.ClientType && errors.ClientType)}
                    sx={requiredLabelSx}
                  >
                    Client Type
                  </InputLabel>
                  <Select
                    
                    labelId="client-type-label"
                    id="client-type-select"
                    value={clientType}
                    label="Client Type"
                    required
                    notched
                    onChange={handleClientType}
                    {...getFieldProps('ClientType')}
                    error={Boolean(touched.ClientType && errors.ClientType)}
                    //disabled={disableField}
                    // helperText={touched.ClientType && errors.ClientType}
                  >
                    
                    <MenuItem value="INDIA">INDIA</MenuItem>
                    <MenuItem value="US">US</MenuItem>

                    {/* <FormHelperText>{touched.timezone && errors.timezone}</FormHelperText> */}
                  </Select>
	                  {touched.ClientType && errors.ClientType && <Typography color="error" variant="body2"  >{touched.ClientType && errors.ClientType}</Typography>}

	                  </FormControl>                 
	                  </Grid>}

	                  {isReseller && (<Grid item md={6}>
	                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-labell">Environment Type</InputLabel>
                  <Select

                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={environmentType}
                    label="Environment Type"
                    onChange={handleEnvironmentType}
                    {...getFieldProps('EnvironmentType')}
                    error={Boolean(touched.EnvironmentType && errors.EnvironmentType)}
                    //disabled={disableField}
                  >
                    <MenuItem sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(143, 148, 148, 0.91)'
                                    }
                                }}  value="1">Production</MenuItem>
                    <MenuItem sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(143, 148, 148, 0.91)'
                                    }
                                }}  value="0">Demo</MenuItem>
                    {/* <FormHelperText>{touched.timezone && errors.timezone}</FormHelperText> */}
                  </Select>
                  {touched.EnvironmentType && errors.EnvironmentType && <Typography color="error" variant="body2"  >{touched.EnvironmentType && errors.EnvironmentType}</Typography>}

                </FormControl>
              </Grid>)}

              <Grid item md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth>
                    <DatePicker
                      label="Monitoring Start Date"
                      value={values.MonitoringStartDate || null}
                      onChange={(newValue) => handleDateChange('MonitoringStartDate', newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="MM/DD/YYYY"  // Add placeholder
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink: true,  // Force shrink to true
                          }}
                          error={Boolean(values.MonitoringStartDate && touched.MonitoringStartDate && errors.MonitoringStartDate)}
                          helperText={values.MonitoringStartDate && touched.MonitoringStartDate && errors.MonitoringStartDate}
                        />
                      )}
                      inputFormat="MM/DD/YYYY"
                      mask="__/__/____"
                    />
                  </FormControl>
                </LocalizationProvider>
              </Grid>

              <Grid item md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth>
                    <DatePicker
                      label="Monitoring End Date"
                      value={values.MonitoringEndDate || null}
                      onChange={(newValue) => handleDateChange('MonitoringEndDate', newValue)}
                      minDate={values.MonitoringStartDate}
                      disabled={!values.MonitoringStartDate}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="MM/DD/YYYY"  // Add placeholder
                          InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink: true,  // Force shrink to true
                          }}
                          error={Boolean(values.MonitoringEndDate && touched.MonitoringEndDate && errors.MonitoringEndDate)}
                          helperText={values.MonitoringEndDate && touched.MonitoringEndDate && errors.MonitoringEndDate}
                        />
                      )}
                      inputFormat="MM/DD/YYYY"
                      mask="__/__/____"
                    />
                  </FormControl>
                </LocalizationProvider>
              </Grid>
              
              {!isReseller && (
                <Grid item md={6}>
                  <TextField
                    fullWidth
                    autoComplete="BillingMonitoringHours"
                    placeholder="Enter Monitoring Hours"
                    label="Billing Monitoring Hours"
                    InputLabelProps={{ shrink: true }}
                    {...getFieldProps('BillingMonitoringHours')}
                    error={Boolean(touched.BillingMonitoringHours && errors.BillingMonitoringHours)}
                    multiline
                    minRows={3}
                  />
	                  {touched.BillingMonitoringHours && errors.BillingMonitoringHours && (
	                    <Typography color="error" variant="body2">{touched.BillingMonitoringHours && errors.BillingMonitoringHours}</Typography>
	                  )}
	                </Grid>
	              )}

	              <Grid item md={6}>
	                <TextField
	                  fullWidth
	                  autoComplete="NoOfCameras"
	                  type="number"
	                  label="No of Cameras"
	                  required
	                  InputLabelProps={{ shrink: true }}
	                  sx={requiredLabelSx}
	                  {...getFieldProps('NoOfCameras')}
	                  error={Boolean((touched.NoOfCameras || submitCount > 0) && errors.NoOfCameras)}
	                  inputProps={{ min: 0 }}
	                />
	                {(touched.NoOfCameras || submitCount > 0) && errors.NoOfCameras && (
	                  <Typography color="error" variant="body2">{errors.NoOfCameras}</Typography>
	                )}
	              </Grid>

	              {!isReseller && (<Grid item md={6}>
	                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-labell">Environment Type</InputLabel>
                  <Select

                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={environmentType}
                    label="Environment Type"
                    onChange={handleEnvironmentType}
                    {...getFieldProps('EnvironmentType')}
                    error={Boolean(touched.EnvironmentType && errors.EnvironmentType)}
                    //disabled={disableField}
                  >
                    <MenuItem sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(143, 148, 148, 0.91)'
                                    }
                                }}  value="1">Production</MenuItem>
                    <MenuItem sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(143, 148, 148, 0.91)'
                                    }
                                }}  value="0">Demo</MenuItem>
                    {/* <FormHelperText>{touched.timezone && errors.timezone}</FormHelperText> */}
                  </Select>
                  {touched.EnvironmentType && errors.EnvironmentType && <Typography color="error" variant="body2"  >{touched.EnvironmentType && errors.EnvironmentType}</Typography>}

                </FormControl>
              </Grid>)}

             



              </Grid>              
              <Typography variant='subtitle2' sx={{ my: 2 }}>Login Info</Typography>
              <Grid container spacing={3}>
                <Grid item md={6}>
	                  <TextField
	                    fullWidth
	                    autoComplete="userId"
	                    type="text"
                    label="Username"
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={requiredLabelSx}
	                    {...getFieldProps('userId')}
	                    error={Boolean((touched.userId || submitCount > 0) && errors.userId)}
	                    disabled={disableField}
	                    helperText={(touched.userId || submitCount > 0) && errors.userId ? errors.userId : ''}
	                  />

	                </Grid>
	                <Grid item md={6}>
                  <TextField
                      fullWidth
                      autoComplete="userPwd"
                      type="text"
                      label="Password"
                      required
                      InputLabelProps={{ shrink: true }}
                      sx={requiredLabelSx}
	                      {...getFieldProps('userPwd')}
	                      error={Boolean((touched.userPwd || submitCount > 0) && errors.userPwd)}
	                      disabled={disableField}
	                      helperText={(touched.userPwd || submitCount > 0) && errors.userPwd ? errors.userPwd : ''}
	                    />

	                </Grid>
              </Grid>
              <Stack direction={"row"} spacing={2} sx={{ my: 2 }}>
                  <Box>
                    <Typography variant='subtitle2'>Event Send To</Typography>
                    <Typography>
                      {values.IsAgentBased === 0
                        ? "Events will be sent to Client"
                        : "Events will be sent to Tier 1"}

                    </Typography>
                  </Box>
                  { values.IsAgentBased ?
                    <Switch color="success" value={values.IsAgentBased} checked={true} onChange={handleSwitch} inputProps={{ 'aria-label': 'controlled' }}/> :
                    <Switch value={values.IsAgentBased} checked={false} onChange={handleSwitch} inputProps={{ 'aria-label': 'controlled' }}/>
                  }            
                  
                  </Stack>

              {(isError.status) && (
                <Alert severity={isError.severity || "error"} sx={{ mb: 2 }}>
                  {isError.msg}
                </Alert>
              )}


              <DialogActions>
              <Button color='error' variant='contained' onClick={handleClose}>Close</Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
              >
                Save 
              </LoadingButton>
              </DialogActions>
              
            </Form>
          </FormikProvider>


    
      </DialogContent>
      {/* <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions> */}
    </Dialog>
  );
}

ClientForm.propTypes = {
  // dialogData: PropTypes.object.isRequired,
  setOpen: PropTypes.func.isRequired
}
